/* eslint-disable no-console */
import {
  ApolloError,
  ApolloQueryResult,
  useApolloClient,
} from '@apollo/client';
import { useFocusEffect } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState } from 'react-native';

import {
  Chat,
  ListChatsPaginationDocument,
  ListChatsPaginationQuery,
} from '@graphql/generated';
import { isValidCursor } from '@hooks/pagination';
import useActiveChat from '@hooks/useActiveChat';
import useChatInput from '@hooks/useChatInput';
import { isChatArchivedInCache } from '@utils/chat/chatArchiveCache';
import { resubscribeDelayMs } from '@utils/chat/subscriptionBackoff';

export type ChatsProps = {
  listChats: Chat[];
  loading: boolean;
  refresh: () => void;
  refreshing: boolean;
  loadMore: () => Promise<void>;
  loadingMore: boolean;
  hasNextPage: boolean;
  fetchChats: () => Promise<void>;
  hasError: boolean;
  hasLoadedOnce: boolean;
  resetError: () => void;
};

type ChatsDataProps = {
  children: (props: ChatsProps) => React.ReactElement;
};

const PAGE_SIZE = 20;
// TA-10922: failed rebuildLoadedPages retries (2s, 4s, 8s, 16s) before
// waiting for the next page-1 result instead.
const MAX_REBUILD_ATTEMPTS = 5;
// MIN_BG_MS is the minimum duration (in milliseconds) the app must be in the background before triggering a refetch.
const MIN_BG_MS = 15000;
// STALE_MS is the maximum time (in milliseconds) since the last sync before data is considered stale.
const STALE_MS = 45000;

// TA-10151: return the list with chatId's unreadCount zeroed. Returns the
// same reference when nothing changed so setState can skip the re-render —
// and only allocates the new array when there is actually something to clear.
export const clearUnreadCountForChat = (
  chats: Chat[],
  chatId: string
): Chat[] => {
  const needsClear = chats.some(
    (c) => c?.id === chatId && (c.unreadCount ?? 0) > 0
  );
  if (!needsClear) return chats;
  return chats.map((c) => (c?.id === chatId ? { ...c, unreadCount: 0 } : c));
};

// TA-10922: the API's cursors are offsets (endCursor "MjA" is base64 "20"),
// so page 1's endCursor is the same whichever chats it holds and cannot tell
// that a row slid off. Compare the set of chat ids instead. Order is ignored
// so a reshuffle inside page 1 doesn't re-fetch later pages.
export const page1Membership = (chats: Chat[]): string =>
  chats
    .map((c) => c?.id)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(',');

const sameRefs = (a: Chat[], b: Chat[]): boolean =>
  a.length === b.length && a.every((c, i) => c === b[i]);

export const chatSort = (a: Chat | undefined, b: Chat | undefined) => {
  const { recentActivityAt: aAt } = a || {};
  const { recentActivityAt: bAt } = b || {};
  const a1 = dayjs(aAt);
  const b1 = dayjs(bAt);
  if (a1 < b1) return 1;
  if (b1 < a1) return -1;
  return 0;
};

const ChatData = ({ children }: ChatsDataProps) => {
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listChats, setListChats] = useState<Chat[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [cursor, setCursor] = useState<string | null | undefined>('');
  const [cachedNextPage, setCachedNextPage] = useState<Chat[]>([]);
  const [isPreloading, setIsPreloading] = useState(false);
  const [discardedPreloads, setDiscardedPreloads] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const paginatedChatIdsRef = useRef<Set<string>>(new Set());
  const loadingMoreRef = useRef(false);
  // Mirror cursor in a ref so handleQueryNext can read latest value without
  // putting cursor in useFocusEffect deps (which would cause a re-subscription
  // on every preload, emitting loading:true and blanking the list on iOS).
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  // TA-10922: which chats the last page-1 result held (see page1Membership).
  // When a later result holds different chats, rows crossed the page-1
  // boundary and everything fetched after page 1 is anchored wrong.
  const page1MembershipRef = useRef<string | undefined>(undefined);
  // Bumped whenever the preloaded page is discarded, so a preload already in
  // flight for the old cursor cannot write its stale page back.
  const preloadGenerationRef = useRef(0);
  // Pending retry of a failed rebuildLoadedPages; cleared on unmount.
  const rebuildRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(
    () => () => {
      if (rebuildRetryTimerRef.current) {
        clearTimeout(rebuildRetryTimerRef.current);
      }
      // A rebuild still in flight has no timer yet; voiding its generation
      // stops it from updating state or scheduling a retry once it settles.
      preloadGenerationRef.current += 1;
    },
    []
  );

  const client = useApolloClient();
  const { subscribeToChatUpdates } = useChatInput();
  const { activeChat, activeChatId } = useActiveChat();
  // TA-10151: the opened chat id must be derived from BOTH context fields.
  // On web, opening a chat from the list goes through WebDrawerContext's
  // navigateToChatDrawer, which calls setActiveChat but never setActiveChatId
  // — listening to activeChatId alone misses the main web open-chat path.
  // activeChat is the canonical opened-chat state on web; activeChatId covers
  // the id-only paths (session restore, deep links).
  const openedChatId = activeChat?.id ?? activeChatId;
  // Mirror openedChatId in a ref for the same reason as cursorRef: the
  // handleQueryNext callback inside useFocusEffect is not recreated when
  // the opened chat changes, so the closure would hold a stale value.
  const openedChatIdRef = useRef(openedChatId);
  openedChatIdRef.current = openedChatId;
  // Mirror subscribeToChatUpdates in a ref so startSubscriptions has no
  // deps and stays reference-stable across renders. useChatInput returns a
  // new function reference on every render (no useCallback inside the hook),
  // which would otherwise make startSubscriptions unstable, causing
  // useFocusEffect to re-fire, re-subscribe the observable, and loop.
  const subscribeToChatUpdatesRef = useRef(subscribeToChatUpdates);
  subscribeToChatUpdatesRef.current = subscribeToChatUpdates;

  // TA-10151: chats beyond page 1 are fetched with no-cache (preloadNextPage)
  // and live here as state snapshots, so the markChatsRead cache update to
  // Chat.unreadCount never reaches them — their badge stayed stuck until a
  // full reload. Opening a chat always marks it read (ChatDetail /
  // ChatMessageData fire markChatsRead), so mirror that into the snapshots.
  //
  // TA-10459: also zero the Apollo cache entry immediately. Any subsequent
  // cache write (e.g. bumpChatToTop from useSendMessage) triggers the
  // watchQuery observable, which reads ALL fields from the cache — including
  // a stale pre-open unreadCount — and overwrites the component state zeroed
  // above. markChatsRead (debounced 500 ms + network RTT) confirms the zero
  // on the server; this makes it effective at the moment the chat opens.
  useEffect(() => {
    if (!openedChatId) return;
    setListChats((prev) => clearUnreadCountForChat(prev, openedChatId));
    setCachedNextPage((prev) => clearUnreadCountForChat(prev, openedChatId));
    const cacheId = client.cache.identify({
      __typename: 'Chat',
      id: openedChatId,
    });
    if (cacheId) {
      client.cache.modify({
        id: cacheId,
        fields: {
          unreadCount: (existing: number | null) =>
            (existing ?? 0) > 0 ? 0 : existing,
        },
      });
    }
  }, [openedChatId, client]);

  const lastSyncAtRef = useRef<number>(Date.now());
  const bgStartAtRef = useRef<number | null>(null);
  const subscriptionUnsubRef = useRef<null | (() => void)>(null);
  const focusMountedRef = useRef(false);

  const queryObservableRef = useRef<ReturnType<
    typeof client.watchQuery<ListChatsPaginationQuery>
  > | null>(null);
  useEffect(() => {
    // errorPolicy: 'all' is scoped here so GraphQL errors returned alongside
    // partial data (HTTP 200 + { errors: [...] }) surface through the next
    // handler rather than being swallowed silently.
    queryObservableRef.current ??= client.watchQuery<ListChatsPaginationQuery>({
      query: ListChatsPaginationDocument,
      variables: { first: PAGE_SIZE, after: null },
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });
    return () => {
      try {
        queryObservableRef.current?.stopPolling?.();
      } catch {
        /* empty */
      }
    };
  }, [client]);

  useEffect(() => {
    if (cursor && cachedNextPage.length === 0) {
      preloadNextPage(cursor);
    }
    // discardedPreloads: a preload discarded by a page-1 reset (TA-10922)
    // was still in flight when the new cursor arrived, so retry after it.
  }, [cursor, cachedNextPage, discardedPreloads]);

  const isNetworkErrorWithStatusCode = (
    error: unknown
  ): error is { statusCode: number } =>
    typeof error === 'object' && error !== null && 'statusCode' in error;

  const checkError = (error: Error): boolean =>
    error instanceof ApolloError &&
    isNetworkErrorWithStatusCode((error as any).networkError) &&
    (error as any).networkError.statusCode === 401;

  const startSubscriptions = useCallback(() => {
    if (subscriptionUnsubRef.current) {
      try {
        subscriptionUnsubRef.current();
      } catch {
        /* empty */
      }
      subscriptionUnsubRef.current = null;
    }
    const unsub = subscribeToChatUpdatesRef.current?.();
    subscriptionUnsubRef.current = unsub || null;
  }, []);

  const stopSubscriptions = useCallback(() => {
    if (subscriptionUnsubRef.current) {
      try {
        subscriptionUnsubRef.current();
      } catch {
        /* empty */
      }
      subscriptionUnsubRef.current = null;
    }
  }, []);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'background' || state === 'inactive') {
        bgStartAtRef.current = Date.now();
      }
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    let prevState: string = AppState.currentState;

    const onAppStateChange = async (nextState: string) => {
      if (/inactive|background/.exec(prevState) && nextState === 'active') {
        const now = Date.now();
        const bgDuration = bgStartAtRef.current
          ? now - bgStartAtRef.current
          : 0;
        const isStale = now - lastSyncAtRef.current > STALE_MS;
        const shouldRefetch = bgDuration > MIN_BG_MS || isStale;

        if (shouldRefetch) {
          startSubscriptions();

          // Delay refetching by 500ms to allow the app to fully transition to the foreground,
          // ensuring that network connectivity and system resources are stable before making requests.
          await new Promise((r) => setTimeout(r, 500));
          try {
            await queryObservableRef.current?.refetch({
              first: PAGE_SIZE,
              after: null,
            });
            lastSyncAtRef.current = Date.now();
          } catch (e) {
            console.error('Resume refetch failed:', e);
          }
        }
      }
      prevState = nextState;
    };

    const sub = AppState.addEventListener('change', onAppStateChange);
    return () => sub.remove();
  }, [client, startSubscriptions]);

  useFocusEffect(
    useCallback(() => {
      const queryObs = queryObservableRef.current;
      if (!queryObs) return () => {};

      // Clear any stale error/result stored in the observable before
      // re-subscribing. Apollo replays the last stored error to new
      // subscribers synchronously via onSubscribe — without this, a
      // cancelled in-flight request from a previous focus session causes
      // a spurious "Failed to fetch" every time the panel re-opens.
      // Apollo 3.x has no public non-deprecated replacement for this. NOSONAR
      queryObs.resetLastResults(); // NOSONAR

      const handleQueryNext = ({ data, loading, errors }: any) => {
        // With errorPolicy:'all', GraphQL errors arrive in the next handler
        // alongside (possibly empty) data. Check errors first — before any
        // data guard — so that an errors-only result (no listChatsPagination)
        // still sets hasError and clears loading rather than silently returning.
        if (errors && errors.length > 0) {
          console.error('[Chats] GraphQL errors in next handler:', errors);
          const malformedCursorError = errors.find(
            (e: any) =>
              typeof e.message === 'string' &&
              (e.message.includes('Invalid pagination cursor') ||
                e.message.includes('malformed keyset cursor'))
          );
          if (malformedCursorError) {
            Sentry.addBreadcrumb({
              category: 'pagination',
              message: `Malformed cursor forwarded: ${cursorRef.current}`,
              level: 'error',
              data: {
                cursor: cursorRef.current,
                error: malformedCursorError.message,
              },
            });
          }
          setLoading(false);
          setRefreshing(false);
          setHasError(true);
          // Don't return — fall through so any partial data (errorPolicy:'all')
          // is still processed and previously loaded chats stay visible.
        }

        if (!data?.listChatsPagination) return;
        setHasLoadedOnce(true);
        setRefreshing(false);
        // Always clear loading when data arrives. Using the observable's
        // `loading` flag here would set loading:true on emissions before
        // network results arrive, causing FlashList refreshing=true and a
        // blank flash on iOS.
        setLoading(false);

        const newChats = (data.listChatsPagination.edges?.map(
          (edge: any) => edge?.node
        ) || []) as Chat[];

        if (newChats.length) {
          const newChatIds = new Set(
            newChats.map((c) => c?.id).filter(Boolean)
          );
          const page1HasNextPage = Boolean(
            data.listChatsPagination.pageInfo.hasNextPage
          );
          setListChats((prev) => {
            const next = filterAndMergeChats(
              prev,
              newChats,
              newChatIds,
              page1HasNextPage
            );
            // If every item is the same reference as before (Apollo cache hit,
            // no data changed), return prev to skip the re-render entirely.
            return sameRefs(next, prev) ? prev : next;
          });
        }

        const page1EndCursor = data.listChatsPagination.pageInfo.endCursor;
        const membership = page1Membership(newChats);
        if (cursorRef.current === '') {
          page1MembershipRef.current = membership;
          const validCursor = isValidCursor(page1EndCursor)
            ? page1EndCursor
            : null;
          setCursor(validCursor);
          setHasNextPage(
            validCursor !== null &&
              data.listChatsPagination.pageInfo.hasNextPage
          );
        } else if (
          // TA-10922: page 1 holds different chats — typically one from
          // further down got a message and pushed page 1's last row to
          // position 21. The preloaded page 2 (no-cache, frozen) was fetched
          // before that, so the pushed-down chat was in neither page and never
          // came back until it had new activity. Re-anchor everything after
          // page 1 on the new page 1 (see onPage1Moved).
          !loading &&
          membership !== page1MembershipRef.current
        ) {
          page1MembershipRef.current = membership;
          onPage1Moved(
            page1EndCursor,
            data.listChatsPagination.pageInfo.hasNextPage,
            newChats
          );
        }

        if (!loading) {
          lastSyncAtRef.current = Date.now();
          // Successful payload with no errors — clear any prior error state so
          // the user automatically recovers without needing to tap retry when
          // polling or app-resume refetches succeed after a transient 500.
          setHasError(false);
        }
      };

      const handleQueryError = (error: any) => {
        setRefreshing(false);
        setLoading(false);
        // 401s are handled centrally: errorLink retries once, then fires
        // setRecentStatusCode(401) → authContext.removeToken(). Calling
        // removeToken() here directly would duplicate the logout (TA-10286).
        if (!checkError(error as Error)) {
          console.error(error);
          setHasError(true);
        }
      };

      // TA-10876: an observable's `error` channel is terminal. When a
      // listChatsPagination fetch fails at the network level (offline blip,
      // 500, auth hiccup), zen-observable closes this subscription, the
      // ObservableQuery loses its last observer and calls tearDownQuery(),
      // which removes the query from QueryManager entirely. From that point:
      //   - no later result ever reaches handleQueryNext, so `hasError` stays
      //     true and the "Couldn't refresh chats" banner never clears;
      //   - `client.refetchQueries({ include: [ListChatsPaginationDocument] })`
      //     — the fallback useChatInput fires for a chatUpdate it could not
      //     place in the cache — matches no registered query, warns, and sends
      //     no request at all.
      // Only a reload or a re-focus recovered. Re-subscribing puts the query
      // back into QueryManager and lets the next successful result clear the
      // error, so the list recovers on its own once the network returns.
      //
      // The re-subscribe is delayed and backed off because subscribing makes
      // this the first observer again, which triggers reobserve() and a fresh
      // network fetch: an immediate re-subscribe would spin a fetch/error loop
      // for as long as the network stays down.
      let cancelled = false;
      let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
      // Zero-based retry counter feeding resubscribeDelayMs — 2s, 4s, 8s…
      // capped at 60s, the same schedule the chatUpdate subscription uses.
      let resubscribeAttempt = 0;
      let querySub: { unsubscribe: () => void } | null = null;

      const onQueryNext = (
        payload: ApolloQueryResult<ListChatsPaginationQuery>
      ) => {
        // A result got through — the connection works, so the next failure
        // starts its backoff from the bottom again.
        resubscribeAttempt = 0;
        handleQueryNext(payload);
      };

      const onQueryError = (error: ApolloError) => {
        handleQueryError(error);
        if (cancelled || resubscribeTimer) return;

        try {
          querySub?.unsubscribe();
        } catch {
          /* empty */
        }
        querySub = null;

        const delay = resubscribeDelayMs(resubscribeAttempt);
        resubscribeAttempt += 1;
        resubscribeTimer = setTimeout(() => {
          resubscribeTimer = null;
          if (cancelled) return;
          // resubscribeAfterError clears the stored error before subscribing
          // and restores it afterwards. Without that, onSubscribe replays the
          // last error to the new subscriber synchronously and closes it again
          // — this is the same helper useQuery uses internally.
          //
          // It is typed on ObservableQuery but carries no stability guarantee,
          // so an Apollo minor could drop it. The plain-subscribe fallback is
          // deliberately a soft landing, not a recovery: on an errored
          // ObservableQuery, onSubscribe replays the stored error to the new
          // subscriber synchronously, which re-enters onQueryError. It cannot
          // spin — the `resubscribeTimer` guard above admits one retry in
          // flight at a time — but it will walk the backoff up to the 60s
          // ceiling and sit there, recovering nothing. The list then behaves
          // exactly as it did before TA-10876: "Try Again" still works,
          // because fetchChats reads refetch()'s result directly instead of
          // going through the subscription. If this branch is ever reached in
          // production, the fix is to port whatever replaced
          // resubscribeAfterError, not to lean on the fallback.
          querySub = queryObs.resubscribeAfterError
            ? queryObs.resubscribeAfterError(onQueryNext, onQueryError)
            : queryObs.subscribe({ next: onQueryNext, error: onQueryError });
        }, delay);
      };

      querySub = queryObs.subscribe({
        next: onQueryNext,
        error: onQueryError,
      });

      startSubscriptions();

      if (focusMountedRef.current) {
        queryObs
          .refetch({ first: PAGE_SIZE, after: null })
          .catch(() => undefined);
      } else {
        focusMountedRef.current = true;
      }

      return () => {
        cancelled = true;
        if (resubscribeTimer) {
          clearTimeout(resubscribeTimer);
          resubscribeTimer = null;
        }
        try {
          querySub?.unsubscribe();
        } catch {
          /* empty */
        }
        stopSubscriptions();
      };
    }, [startSubscriptions, stopSubscriptions])
  );

  // fetchChats rethrows after setting hasError so that resetError can
  // distinguish a failed retry from a successful one.
  const fetchChats = async () => {
    if (!queryObservableRef.current) return;

    try {
      setLoading(true);

      const result = await queryObservableRef.current.refetch({
        first: PAGE_SIZE,
        after: null,
      });

      if (result.error) {
        setHasError(true);
        throw result.error;
      }

      const edges = result?.data?.listChatsPagination?.edges || [];
      const newChats = edges
        .map((e: any) => e?.node)
        .filter((c: any): c is Chat => c != null);
      // Merge page-1 results with already-paginated items rather than replacing.
      // Replacing collapses the list from N items to 20, causing FlashList to
      // recycle N-20 cells whose native views flash blank during remapping.
      // paginatedChatIdsRef is intentionally NOT cleared — those items remain
      // valid and must survive the next observable poll's filter step.
      const newChatIds = new Set(
        newChats.map((c: Chat) => c?.id).filter(Boolean)
      );
      const page1HasNextPage = Boolean(
        result?.data?.listChatsPagination?.pageInfo.hasNextPage
      );
      setListChats((prevChats) =>
        filterAndMergeChats(prevChats, newChats, newChatIds, page1HasNextPage)
      );
      // If page 1 ends on a new row, everything after it is re-anchored
      // (onPage1Moved, TA-10922). Otherwise only update cursor/hasNextPage
      // and clear cachedNextPage when the user hasn't loaded extra pages.
      // When pages 2+ are already in the list, preloadNextPage has already
      // advanced cursor to the endCursor of the cached page. Clearing
      // cachedNextPage while cursor points past it drops a whole page —
      // the next preload would fetch page N+2, skipping page N+1.
      // Preserving both cursor and cachedNextPage lets loadMore serve the
      // pre-fetched page without gaps.
      const page1EndCursor =
        result?.data?.listChatsPagination?.pageInfo.endCursor;
      const membership = page1Membership(newChats);
      if (membership !== page1MembershipRef.current) {
        page1MembershipRef.current = membership;
        onPage1Moved(page1EndCursor, page1HasNextPage, newChats);
      } else if (paginatedChatIdsRef.current.size === 0) {
        resetNextPage(page1EndCursor, page1HasNextPage);
      }
      lastSyncAtRef.current = Date.now();
      setHasLoadedOnce(true);
      setHasError(false);
    } catch (error) {
      // 401 handled centrally by errorLink → authContext (TA-10286).
      if (!checkError(error as Error)) {
        console.error('Error fetching chats:', error);
        setHasError(true);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  // resetError clears the error flag then retries. hasError is cleared
  // optimistically so the fallback UI dismisses immediately on tap; if
  // fetchChats throws, it re-sets hasError(true) so the fallback reappears.
  const resetError = useCallback(() => {
    setHasError(false);
    fetchChats().catch(() => undefined);
  }, [fetchChats]);

  const refresh = () => {
    setRefreshing(true);
    fetchChats()
      .then(() => setHasError(false))
      .catch(() => undefined)
      .finally(() => setRefreshing(false));
  };

  const cachedNextPageRef = useRef(cachedNextPage);
  useEffect(() => {
    cachedNextPageRef.current = cachedNextPage;
  }, [cachedNextPage]);

  // Point pagination at the page after the given page-1 result and drop the
  // preloaded page, which was fetched before page 1 changed.
  const resetNextPage = (
    page1EndCursor: string | null | undefined,
    page1HasNextPage: boolean | null | undefined
  ) => {
    preloadGenerationRef.current += 1;
    setCachedNextPage([]);
    const validCursor = isValidCursor(page1EndCursor) ? page1EndCursor : null;
    setCursor(validCursor);
    setHasNextPage(validCursor !== null && Boolean(page1HasNextPage));
  };

  // TA-10922: page 1 holds different chats, so whatever was fetched after the
  // old page 1 is out of step with it.
  const onPage1Moved = (
    page1EndCursor: string | null | undefined,
    page1HasNextPage: boolean | null | undefined,
    page1Chats: Chat[]
  ) => {
    if (paginatedChatIdsRef.current.size === 0) {
      resetNextPage(page1EndCursor, page1HasNextPage);
    } else {
      rebuildLoadedPages(page1EndCursor, page1HasNextPage, page1Chats).catch(
        () => undefined
      );
    }
  };

  // With extra pages shown, re-fetch the same number of rows after the new
  // page 1 and make that the list after page 1. The server is the only
  // reliable answer: a row missing from page 1 may have slid to 21 (keep it)
  // or been left/deleted (drop it), and sort order cannot tell the two apart.
  // Until this lands, keepSlidOffChat holds the row so the list doesn't flash.
  const rebuildLoadedPages = async (
    page1EndCursor: string | null | undefined,
    page1HasNextPage: boolean | null | undefined,
    page1Chats: Chat[],
    attempt = 0
  ): Promise<void> => {
    if (rebuildRetryTimerRef.current) {
      clearTimeout(rebuildRetryTimerRef.current);
      rebuildRetryTimerRef.current = null;
    }
    preloadGenerationRef.current += 1;
    const generation = preloadGenerationRef.current;

    // page1MembershipRef already records this page 1, so a repeat of the
    // same result won't start another rebuild — a failure must retry on its
    // own or the loaded pages stay anchored on the old page 1. Back off like
    // the other chat-list recoveries; after the last attempt, forget the
    // membership so the next page-1 result (focus, resume, chatUpdate) tries
    // again.
    const retryLater = () => {
      if (generation !== preloadGenerationRef.current) return;
      if (attempt + 1 >= MAX_REBUILD_ATTEMPTS) {
        page1MembershipRef.current = undefined;
        return;
      }
      rebuildRetryTimerRef.current = setTimeout(() => {
        rebuildRetryTimerRef.current = null;
        if (generation !== preloadGenerationRef.current) return;
        rebuildLoadedPages(
          page1EndCursor,
          page1HasNextPage,
          page1Chats,
          attempt + 1
        ).catch(() => undefined);
      }, resubscribeDelayMs(attempt));
    };

    const target = paginatedChatIdsRef.current.size;
    const rebuilt: Chat[] = [];
    let after = isValidCursor(page1EndCursor) ? page1EndCursor : null;
    let more = after !== null && Boolean(page1HasNextPage);
    try {
      while (more && rebuilt.length < target) {
        const result = await client.query<ListChatsPaginationQuery>({
          query: ListChatsPaginationDocument,
          variables: { first: PAGE_SIZE, after },
          fetchPolicy: 'no-cache',
        });
        // A newer page-1 move started its own rebuild.
        if (generation !== preloadGenerationRef.current) return;
        const res = result?.data?.listChatsPagination;
        if (!res) {
          retryLater();
          return;
        }
        res.edges?.forEach(
          (edge) => edge?.node && rebuilt.push(edge.node as Chat)
        );
        const next = res.pageInfo.endCursor;
        after = isValidCursor(next) ? next : null;
        more = after !== null && res.pageInfo.hasNextPage;
      }
    } catch (error) {
      // Leave the list and cursor as they were until a retry lands.
      console.error('Error rebuilding loaded chat pages:', error);
      retryLater();
      return;
    }

    const page1Ids = new Set(page1Chats.map((c) => c?.id).filter(Boolean));
    const rebuiltIds = new Set(
      rebuilt.map((c) => c?.id).filter(Boolean) as string[]
    );
    paginatedChatIdsRef.current = rebuiltIds;
    setListChats((prev) => {
      const kept = prev.filter(
        (c) =>
          page1Ids.has(c?.id) ||
          rebuiltIds.has(c?.id) ||
          (openedChatIdRef.current != null && c?.id === openedChatIdRef.current)
      );
      // TA-9295: a row already in the list is cache-backed and fresher than
      // this no-cache copy, so only add the ones it doesn't hold.
      const present = new Set(kept.map((c) => c?.id));
      const additions = rebuilt.filter((c) => !present.has(c.id));
      return [...kept, ...additions];
    });
    preloadGenerationRef.current += 1;
    setCachedNextPage([]);
    setCursor(after);
    setHasNextPage(more);
  };

  const preloadNextPage = async (nextCursor: string) => {
    if (!hasNextPage || isPreloading || cachedNextPage.length > 0) return;
    setIsPreloading(true);
    const generation = preloadGenerationRef.current;
    try {
      // no-cache: prevents relayStylePagination from merging page 2 into the
      // watchQuery cache, which would emit early and grow listChats before
      // the user triggers loadMore — causing blank rows / white screen on iOS.
      const result = await client.query<ListChatsPaginationQuery>({
        query: ListChatsPaginationDocument,
        variables: { first: PAGE_SIZE, after: nextCursor },
        fetchPolicy: 'no-cache',
      });
      const res = result?.data?.listChatsPagination;
      // TA-10922: page 1 moved on while this was in flight; the new cursor
      // is already set and `finally` schedules the re-preload.
      if (generation !== preloadGenerationRef.current) return;
      if (res) {
        const newChats = (res.edges?.map((edge: any) => edge?.node) ||
          []) as Chat[];
        setCachedNextPage(newChats);
        const nextCursor = res.pageInfo.endCursor;
        const validCursor = isValidCursor(nextCursor) ? nextCursor : null;
        setCursor(validCursor);
        setHasNextPage(validCursor !== null && res.pageInfo.hasNextPage);
      }
    } catch (error) {
      console.error('Error preloading next page:', error);
    } finally {
      setIsPreloading(false);
      // The preload effect ran for the new cursor while this one was still
      // in flight, and skipped it. Re-run it whether this request resolved or
      // rejected, or page 2 stays empty and loadMore times out.
      if (generation !== preloadGenerationRef.current) {
        setDiscardedPreloads((n) => n + 1);
      }
    }
  };

  const mergeUniqueChats = (prevChats: Chat[], newChats: Chat[]): Chat[] => {
    const map = new Map<string, Chat>();
    prevChats.forEach((c) => c?.id && map.set(c.id, c));
    newChats.forEach((c) => c?.id && map.set(c.id, c));
    return Array.from(map.values());
  };

  // A chat missing from a page-1 response is kept when it came from a later
  // page or is the one the user has open — otherwise pull-to-refresh would
  // collapse a paginated list back to 20 rows.
  //
  // TA-10878: that rule also kept a chat the user had just ARCHIVED, which is
  // why its row stayed in the list. The archived chat is gone from the cached
  // connection (applyChatArchiveState) but these kept entries are snapshots
  // taken before the archive — page 2+ entries are fetched `no-cache` and
  // never update at all — so the only current answer is the cache's own
  // `settingsArchivedAt` on the Chat entity.
  const keepMissingChat = (chat: Chat): boolean =>
    !isChatArchivedInCache(client.cache, chat?.id) &&
    (paginatedChatIdsRef.current.has(chat?.id) ||
      (openedChatIdRef.current != null &&
        chat?.id === openedChatIdRef.current));

  // TA-10922: interim hold while rebuildLoadedPages is in flight. With extra
  // pages shown, a chat pushed off the end of page 1 would otherwise vanish
  // for a round trip and pop back. Hold a row that sorts after page 1's
  // oldest unpinned row (where a pushed-down chat goes); the rebuild then
  // keeps or drops it from what the server actually returns, so a left or
  // deleted tail chat is not held for good.
  const keepSlidOffChat = (
    chat: Chat,
    newChats: Chat[],
    page1HasNextPage: boolean
  ): boolean => {
    if (!page1HasNextPage || paginatedChatIdsRef.current.size === 0) {
      return false;
    }
    if (!chat || chat.settingsPin) return false;
    if (isChatArchivedInCache(client.cache, chat.id)) return false;
    const unpinned = newChats.filter((c) => c && !c.settingsPin);
    if (unpinned.length === 0) return false;
    const oldest = unpinned.reduce(
      (acc, c) => (chatSort(acc, c) > 0 ? acc : c),
      unpinned[0]
    );
    return chatSort(oldest, chat) <= 0;
  };

  const filterAndMergeChats = (
    prevChats: Chat[],
    newChats: Chat[],
    newChatIds: Set<string | undefined>,
    page1HasNextPage: boolean
  ): Chat[] => {
    const kept = prevChats.filter(
      (c) =>
        newChatIds.has(c?.id) ||
        keepMissingChat(c) ||
        keepSlidOffChat(c, newChats, page1HasNextPage)
    );
    return mergeUniqueChats(kept, newChats);
  };

  const loadMore = async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      if (cachedNextPageRef.current.length === 0) {
        await waitForPreloadingToFinish(5000);
      }
      cachedNextPageRef.current.forEach(
        (c) => c?.id && paginatedChatIdsRef.current.add(c.id)
      );
      // TA-9295: append only chats the list does not already hold. The cached
      // page is fetched no-cache (see preloadNextPage) and frozen at preload
      // time, so no later cache write reaches it — while a row already in the
      // list is cache-backed and kept current by the watchQuery observable.
      // Letting the snapshot win for a duplicate put a stale row back: a chat
      // preloaded as part of page 2 and then bumped into page 1 by a new
      // message had its unread badge wiped on the next scroll, survived pull
      // to refresh (the following loadMore re-applied the snapshot) and
      // cleared only on an app restart.
      setListChats((prev) => {
        const present = new Set(
          prev.map((c) => c?.id).filter(Boolean) as string[]
        );
        const additions = cachedNextPageRef.current.filter(
          (c) => c?.id && !present.has(c.id)
        );
        return additions.length ? [...prev, ...additions] : prev;
      });
      setCachedNextPage([]);
    } catch (error) {
      console.error('Error displaying cached chats:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const waitForPreloadingToFinish = async (timeoutMs = 5000): Promise<void> => {
    return new Promise<void>((resolve) => {
      const start = Date.now();
      const check = () => {
        const elapsed = Date.now() - start;
        if (cachedNextPageRef.current.length > 0) resolve();
        else if (elapsed >= timeoutMs) resolve();
        else setTimeout(check, 100);
      };
      check();
    });
  };

  const sortedListChats = useMemo(() => {
    const valid = listChats.filter((i): i is Chat => i != null);
    const pinned = valid.filter((i) => i.settingsPin);
    const nonPinned = valid.filter((i) => !i.settingsPin);
    return [...pinned.sort(chatSort), ...nonPinned.sort(chatSort)];
  }, [listChats]);

  return children({
    listChats: sortedListChats,
    loading,
    refresh,
    refreshing,
    loadMore,
    loadingMore,
    hasNextPage: hasNextPage || cachedNextPage.length > 0,
    fetchChats,
    hasError,
    hasLoadedOnce,
    resetError,
  });
};

export const ChatsData = Sentry.withProfiler(ChatData);
